import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Modell, GeoEinstellungen, KonfigDatei, Fachbereich, Haus,
} from '../api';
import { Hauspflege, ModellHaus } from './Haeuser';
import { istMehrhaus } from '../mehrhaus';
import { KonfigRollen } from '../api';
import { Grunddaten } from './Grunddaten';
import { IconDatei, IconLage, IconPlus } from '../Icons';
import { Fehler } from './Projekte';
import { Zeitplaene } from './Zeitplaene';
import { Regelaufgaben } from './Regelaufgaben';
import { Regelbetrieb } from './Regelbetrieb';
import { Projektrechte } from './Projektrechte';
import type { Rahmen } from './Projektrahmen';
import { PageHeader } from '../rahmen/PageHeader';
import { ContentSection, EmptyState, ErrorState, LoadingState } from '../muster';

/**
 * Reiter „Einstellungen".
 *
 * Vier Gruppen, von oben nach unten in der Reihenfolge, in der sie beim
 * Einrichten eines Projekts gebraucht werden: welches Modell welche Daten
 * traegt, welche Konfigurationsdateien gelten, wie IFC-Dateien im Raum liegen,
 * und was wiederkehrend laufen soll.
 *
 * Die Modellzuordnung steht oben, weil sie die haeufigste Ursache dafuer ist,
 * dass eine Aufnahme nichts liefert - und das faellt sonst erst nach zehn
 * Minuten Wartezeit auf.
 */
export function Einstellungen() {
  const {
    api, projekt, bereiche, profile, neuLaden, haeuser = [], haeuserNeuLaden,
  } = useOutletContext<Rahmen>();

  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={8} spalten={3} />;

  return (
    <>
      <PageHeader
        titel="Einstellungen"
        beschreibung={'Welches Modell welche Daten trägt, welche Konfigurationsdateien '
          + 'gelten, wie IFC-Dateien im Raum liegen und was wiederkehrend laufen soll.'}
        meta={`${projekt.modelle.length} Modell(e) · ${bereiche.length} Fachbereich(e)`}
      />

      {/* Ganz oben, weil sie bestimmt, was es überhaupt zu konfigurieren
          gibt: Ein abgeschalteter Fachbereich hat keinen Reiter, und dann ist
          auch seine Modellzuordnung gegenstandslos. */}
      <Fachbereichsfreischaltung
        api={api}
        projektId={projekt.id}
        bereiche={bereiche}
        neuLaden={neuLaden}
      />

      {/* PT-0a (Projekttrennung, F39): wer dieses Projekt sieht. Nur mit
          Pflegerecht sichtbar (SystemAdmin, ProjectAdmin mit Verwaltungsrecht). */}
      <Projektrechte api={api} projektId={projekt.id} rechte={projekt.rechte} neuLaden={neuLaden} />

      <Modellzuordnung
        api={api}
        projektId={projekt.id}
        modelle={projekt.modelle}
        bereiche={bereiche}
        neuLaden={() => { neuLaden(); haeuserNeuLaden?.(); }}
        haeuser={haeuser}
      />

      {/* F60 H2b: die Häuser gleich unter der Modellzuordnung - beides beantwortet „welches Modell liefert was, wo“. */}
      <Hauspflege
        api={api}
        projektId={projekt.id}
        haeuser={haeuser}
        neuLaden={() => { haeuserNeuLaden?.(); }}
      />

      {/* Die Grunddaten zuerst: Sie gelten für alle Modelle und sind das, was beim
          Einrichten eines Projekts zuerst gebraucht wird. */}
      <Grunddaten api={api} projektId={projekt.id} />

      {/* Gleich hinter den Grunddaten: Beide handeln von Feldern. Die
          Grunddaten sind die Felder EINES Katalogs, der Füllgrad die Frage,
          wie weit ALLE Kataloge gefüllt sind (Vertiefungsplan V-P1).

          Die Auswertung selbst steht seit dem 20.09.2026 auf einer eigenen
          Seite und in der Seitenleiste unter „Projekt" (SL4): Sie ist eine
          Auswertung über alle sieben Gewerkekataloge und gehörte nie
          zwischen Modellzuordnung und Lagekorrektur. Hier bleibt der
          Verweis, damit niemand sie dort sucht, wo sie bisher stand. */}
      <ContentSection
        titel="Parameterfüllgrad"
        beschreibung={'Wie weit die Gewerkekataloge gefüllt sind — über alle '
          + 'Gewerke. Steht jetzt auf einer eigenen Seite.'}
      >
        <Link to={`/projekte/${projekt.id}/parameter-fuellgrad`}>
          Parameterfüllgrad öffnen
        </Link>
      </ContentSection>

      <Konfigurationsdateien api={api} projektId={projekt.id} />

      <Lagekorrektur
        api={api}
        projektId={projekt.id}
        werte={projekt.geo}
        neuLaden={neuLaden}
      />

      {/* Der Betrieb GLEICH VOR den Zeitplänen: Was hier rot ist, kommt in
          aller Regel aus einem wiederkehrenden Export — und die Frage „läuft
          das noch?" stellt sich vor der Frage „was läuft wann?". */}
      <Regelbetrieb api={api} projektId={projekt.id} />

      <Zeitplaene
        api={api}
        projektId={projekt.id}
        modelle={projekt.modelle}
        profile={profile}
      />

      {/* WE1 (Eigentümer 22.10.2026): die Ketten der Modellaufnahmen mit ihren
          Folgeläufen - unter den Einzel-Zeitplänen, weil sie dieselbe Frage
          beantworten („was läuft wann?“), nur für eine ganze Nacht. */}
      <Regelaufgaben api={api} projektId={projekt.id} profile={profile} />
    </>
  );
}

/**
 * Welche Fachbereiche dieses Projekt führt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DAS HIER STEHT UND NICHT IM KATALOG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Fachbereichskatalog ist gemeinsam — er beschreibt, WAS es gibt. Diese
 * Seite entscheidet, was HIER gilt.
 *
 * Der Unterschied ist der Grund, warum ein Projekt eine eigene Gewerkeseite
 * oder einen weiteren Import bekommen kann, ohne dass ein zweiter Quellzweig
 * entsteht: Der Code liegt gemeinsam, die Erreichbarkeit ist Konfiguration.
 * Zwei Quellbäume hätten bedeutet, dass jede Korrektur zweimal gemacht werden
 * muss — und die vergessene fällt erst Wochen später auf.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * OHNE EINTRAG IST ES FREIGESCHALTET
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein frisch angelegtes Projekt hat keine Zeilen und sieht trotzdem alles,
 * was umgesetzt ist. Ein leerer Stand, der alles versteckt, sähe wie ein
 * kaputtes Projekt aus.
 */
function Fachbereichsfreischaltung({ api, projektId, bereiche, neuLaden }: {
  api: ApiClient; projektId: string; bereiche: Fachbereich[];
  neuLaden: () => void;
}) {
  const [katalog, setKatalog] = useState<Fachbereich[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // Der KATALOG, nicht die Projektliste: Abgeschaltetes muss wählbar bleiben,
  // sonst kommt man nicht mehr daran.
  useEffect(() => {
    api.fachbereiche()
      .then((k) => setKatalog(k.filter((b) => b.istUmgesetzt)))
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api]);

  useEffect(() => {
    setGewaehlt(new Set(bereiche.map((b) => b.roleKey)));
  }, [bereiche]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Der Fachbereichskatalog konnte nicht geladen werden."
        technisch={fehler}
        beiErneutVersuchen={() => {
          setFehler(null);
          api.fachbereiche()
            .then((k) => setKatalog(k.filter((b) => b.istUmgesetzt)))
            .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
        }}
      />
    );
  }
  if (!katalog) return <LoadingState text="Fachbereiche werden geladen …" zeilen={4} spalten={3} />;

  const umschalten = (schluessel: string) => {
    setGewaehlt((vorher) => {
      const neu = new Set(vorher);
      if (neu.has(schluessel)) neu.delete(schluessel); else neu.add(schluessel);
      return neu;
    });
  };

  const speichern = async () => {
    setLaeuft(true);
    setMeldung(null);

    try {
      await api.projektFachbereicheSetzen(projektId, [...gewaehlt]);
      setMeldung('gespeichert — die Reiter richten sich beim nächsten Laden danach');
      neuLaden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const geaendert =
    gewaehlt.size !== bereiche.length
    || bereiche.some((b) => !gewaehlt.has(b.roleKey));

  return (
    <section className="karte">
      <h2>Fachbereiche dieses Projekts</h2>
      <p className="schwach" style={{ margin: '0 0 10px', fontSize: 12 }}>
        Was hier abgeschaltet ist, hat keinen Reiter. Der Katalog bleibt
        gemeinsam — ein Projekt kann damit eine eigene Gewerkeseite oder einen
        weiteren Import führen, ohne einen zweiten Quellstand.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {katalog.map((b) => (
          <label key={b.roleKey}
                 title={b.beschreibung ?? undefined}
                 style={{ display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 10px', borderRadius: 3, fontSize: 13,
                          border: '1px solid var(--rand)',
                          background: gewaehlt.has(b.roleKey)
                            ? 'var(--flaeche)' : 'transparent',
                          opacity: gewaehlt.has(b.roleKey) ? 1 : 0.55,
                          cursor: 'pointer' }}>
            <input type="checkbox"
                   checked={gewaehlt.has(b.roleKey)}
                   onChange={() => umschalten(b.roleKey)} />
            {b.bezeichnung}
          </label>
        ))}
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 10,
                    alignItems: 'center' }}>
        <button onClick={speichern} disabled={laeuft || !geaendert}
                className={geaendert ? 'betont' : undefined}>
          {laeuft ? 'speichert …' : 'Freischaltung speichern'}
        </button>
        {gewaehlt.size === 0 && (
          <span style={{ color: 'var(--warnung)', fontSize: 12 }}>
            Ohne einen einzigen Fachbereich bleibt die Projektansicht leer.
          </span>
        )}
        {meldung && <span className="schwach" style={{ fontSize: 12 }}>{meldung}</span>}
      </div>
    </section>
  );
}

/**
 * Kreuztabelle Modell gegen Fachbereich.
 *
 * Zeilen sind die aktiven Modelle, Spalten die Fachbereiche. Ein Haken sagt:
 * hier liegen diese Daten. Mehrere Modelle je Bereich sind zugelassen - bei
 * einem Projekt mit mehreren Bauabschnitten ist das der Regelfall.
 *
 * Vorher standen zwei Haken in der Modellzeile der Uebersicht. Bei neun
 * Fachbereichen und fuenf Modellen ist eine Kreuztabelle die einzige Form, in
 * der man sieht, wo etwas fehlt.
 */
function Modellzuordnung({
  api, projektId, modelle, bereiche, neuLaden, haeuser = [],
}: {
  api: ApiClient;
  projektId: string;
  modelle: Modell[];
  bereiche: Fachbereich[];
  neuLaden: () => void;
  /** F60 H2b: ab zwei aktiven Häusern eine Spalte „Haus“ mit Auswahl je Modell. */
  haeuser?: Haus[];
}) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const aktive = modelle.filter((m) => m.isActive);
  const rollen = bereiche.filter((b) => b.istQuellRolle);
  const mitHaus = istMehrhaus(haeuser);

  const hausSetzen = async (modell: Modell, hausId: string | null) => {
    setLaeuft(`${modell.id}:haus`);
    setFehler(null);
    try {
      await api.modellHausSetzen(projektId, modell.id, hausId);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const umschalten = async (modell: Modell, rolleKey: string) => {
    const vorher = modell.rollen ?? [];
    const nachher = vorher.includes(rolleKey)
      ? vorher.filter((r) => r !== rolleKey)
      : [...vorher, rolleKey];

    setLaeuft(`${modell.id}:${rolleKey}`);
    setFehler(null);
    try {
      await api.modellRollen(projektId, modell.id, nachher);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <div className="karte">
      <h2 className="karte-titel"><IconDatei />Modellzuordnung</h2>
      <p style={{ marginTop: 0 }} className="schwach">
        In welchem Modell welche Daten liegen. Nur wo ein Haken steht, lässt sich
        eine Aufnahme beauftragen.
      </p>

      {fehler && (
        <div className="hinweis fehler">
          <span className="hinweis-zeichen">!</span>{fehler}
        </div>
      )}

      {aktive.length === 0 ? (
        <EmptyState
          titel="Dieses Projekt hat kein aktives Modell."
          erklaerung={'Ohne Modell gibt es keine Datenquelle — unter „Übersicht" ein '
            + 'Modell aus ACC anlegen und aktiv setzen.'}
        />
      ) : (
        <div className="tabellenrahmen">
          <table className="zuordnung">
            <thead>
              <tr>
                <th>Modell</th>
                {mitHaus && <th>Haus</th>}
                {rollen.map((b) => (
                  <th key={b.roleKey} title={b.beschreibung ?? undefined}>
                    {b.bezeichnung}
                    {!b.istUmgesetzt && <span className="reiter-marke">vorb.</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aktive.map((m) => (
                <tr key={m.id}>
                  <td className="zuordnung-name">{m.name}</td>
                  {mitHaus && (
                    <td>
                      <ModellHaus
                        modell={m}
                        haeuser={haeuser}
                        laeuft={laeuft === `${m.id}:haus`}
                        setzen={(hausId) => hausSetzen(m, hausId)}
                      />
                    </td>
                  )}
                  {rollen.map((b) => {
                    const schluessel = `${m.id}:${b.roleKey}`;
                    const gesetzt = (m.rollen ?? []).includes(b.roleKey);
                    return (
                      <td key={b.roleKey} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={gesetzt}
                          disabled={laeuft === schluessel}
                          onChange={() => umschalten(m, b.roleKey)}
                          aria-label={`${m.name}: ${b.bezeichnung}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OhneQuelle modelle={aktive} rollen={rollen} />
    </div>
  );
}

/**
 * Nennt die Fachbereiche, fuer die kein Quellmodell markiert ist.
 *
 * Nur die umgesetzten - bei den vorbereiteten ist es kein Mangel, sondern der
 * erwartete Zustand.
 */
function OhneQuelle({
  modelle, rollen,
}: {
  modelle: Modell[];
  rollen: Fachbereich[];
}) {
  const fehlend = rollen.filter(
    (b) => b.istUmgesetzt && !modelle.some((m) => (m.rollen ?? []).includes(b.roleKey)),
  );

  if (fehlend.length === 0) return null;

  return (
    <div className="hinweis warnung" style={{ marginTop: 14 }}>
      <span className="hinweis-zeichen">!</span>
      <div>
        Ohne Quellmodell: <strong>{fehlend.map((b) => b.bezeichnung).join(', ')}</strong>.
        Dort ist keine Aufnahme möglich, bis ein Modell markiert ist.
      </div>
    </div>
  );
}

function Konfigurationsdateien({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [dateien, setDateien] = useState<KonfigDatei[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hochladen, setHochladen] = useState(false);

  const laden = () =>
    api.konfigDateien()
      .then(setDateien)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));

  useEffect(() => { laden(); }, [api]);

  if (fehler) return <div className="karte"><Fehler text={fehler} /></div>;

  // Projektbezogene zuerst, danach die hausweiten Vorlagen.
  const eigene = dateien?.filter((d) => d.projectId === projektId) ?? [];
  const vorlagen = dateien?.filter((d) => d.projectId === null) ?? [];

  return (
    <div className="karte">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="karte-titel" style={{ margin: 0 }}><IconDatei />Konfigurationsdateien</h2>
        <button onClick={() => setHochladen(!hochladen)}>
          {hochladen ? 'Abbrechen' : <><IconPlus size={16} />Datei hochladen</>}
        </button>
      </div>

      {hochladen && (
        <KonfigHochladen
          api={api}
          projektId={projektId}
          fertig={() => { setHochladen(false); laden(); }}
        />
      )}

      {!dateien ? (
        <div className="leer">wird geladen …</div>
      ) : (
        <>
          {eigene.length > 0 && (
            <Dateiliste titel="Für dieses Projekt" dateien={eigene} />
          )}
          {vorlagen.length > 0 && (
            <Dateiliste titel="Hausweite Vorlagen" dateien={vorlagen} />
          )}
          {eigene.length === 0 && vorlagen.length === 0 && (
            <div className="leer">Noch keine Konfigurationsdateien.</div>
          )}
        </>
      )}

      <p className="sehr-schwach" style={{ maxWidth: 680, marginBottom: 0 }}>
        Zugeordnet werden die Dateien im Exportprofil, nicht hier. Ein Profil
        verweist über den Schlüssel darauf - so lässt sich dieselbe Datei in
        mehreren Profilen verwenden.
      </p>
    </div>
  );
}


function Dateiliste({ titel, dateien }: { titel: string; dateien: KonfigDatei[] }) {
  return (
    <>
      <p className="sehr-schwach" style={{ marginTop: 16, marginBottom: 6 }}>{titel}</p>
      <table>
        <thead>
          <tr><th>Schlüssel</th><th>Art</th><th>Datei</th><th>Version</th><th></th></tr>
        </thead>
        <tbody>
          {dateien.map((d) => {
            const rolle = KonfigRollen.find((r) => r.wert === d.kind);
            return (
              <tr key={d.id}>
                <td><strong>{d.key}</strong></td>
                <td className="schwach">{rolle?.text ?? d.kind}</td>
                <td className="schwach">{d.fileName}</td>
                <td className="schwach zahlen">{d.version}</td>
                <td style={{ textAlign: 'right' }}>
                  <span className="sehr-schwach">{(d.sizeBytes / 1024).toFixed(0)} KB</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}


function KonfigHochladen({
  api, projektId, fertig,
}: {
  api: ApiClient;
  projektId: string;
  fertig: () => void;
}) {
  const [datei, setDatei] = useState<File | null>(null);
  const [schluessel, setSchluessel] = useState('');
  const [art, setArt] = useState('ifc.pset');
  const [version, setVersion] = useState('1.0.0');
  const [fuerProjekt, setFuerProjekt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  // Aus dem Dateinamen einen brauchbaren Schluessel vorschlagen: Kleinschrift,
  // nur Buchstaben, Ziffern und Bindestriche.
  const dateiGewaehlt = (f: File | null) => {
    setDatei(f);
    if (f && !schluessel) {
      const roh = f.name.replace(/\.[^.]+$/, '');
      const vorschlag = roh
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSchluessel(vorschlag);
    }
  };

  const speichern = async () => {
    if (!datei) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await api.konfigHochladen({
        datei,
        key: schluessel.trim(),
        kind: art,
        version: version.trim(),
        projektId: fuerProjekt ? projektId : undefined,
      });
      fertig();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setLaeuft(false);
    }
  };

  const hinweis = KonfigRollen.find((r) => r.wert === art)?.hinweis;

  return (
    <div style={{ background: 'var(--flaeche)', padding: 16, borderRadius: 3, marginTop: 14 }}>
      {fehler && <Fehler text={fehler} />}

      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, maxWidth: 680 }}>
        <label>Datei</label>
        <input
          type="file"
          accept=".txt"
          onChange={(e) => dateiGewaehlt(e.target.files?.[0] ?? null)}
        />

        <label>Art</label>
        <div>
          <select value={art} onChange={(e) => setArt(e.target.value)}>
            {KonfigRollen.map((r) => (
              <option key={r.wert} value={r.wert}>{r.text}</option>
            ))}
          </select>
          {hinweis && <p className="sehr-schwach" style={{ margin: '4px 0 0' }}>{hinweis}</p>}
        </div>

        <label>Schlüssel</label>
        <div>
          <input value={schluessel} onChange={(e) => setSchluessel(e.target.value)}
                 placeholder="pset-tuv-2026-01" />
          <p className="sehr-schwach" style={{ margin: '4px 0 0' }}>
            Darueber verweist das Exportprofil auf die Datei.
          </p>
        </div>

        <label>Version</label>
        <div>
          <input value={version} onChange={(e) => setVersion(e.target.value)}
                 style={{ width: 120 }} />
          <p className="sehr-schwach" style={{ margin: '4px 0 0' }}>
            Dateien sind unveraenderlich - eine Änderung braucht eine neue Version.
          </p>
        </div>

        <label>Geltungsbereich</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={fuerProjekt}
                 onChange={(e) => setFuerProjekt(e.target.checked)} />
          <span>nur für dieses Projekt</span>
        </label>
      </div>

      <p className="sehr-schwach" style={{ maxWidth: 680 }}>
        Ohne Haken steht die Datei allen Projekten zur Verfügung. Das ist der
        Regelfall: Eigenschaftensaetze und Layerzuordnungen sind meist hausweite
        Vorgaben.
      </p>

      <div style={{ marginTop: 12 }}>
        <button className="betont" onClick={speichern}
                disabled={!datei || !schluessel.trim() || laeuft}>
          {laeuft ? 'wird hochgeladen …' : 'Hochladen'}
        </button>
      </div>
    </div>
  );
}

// --- Modelle ----------------------------------------------------------------


function Lagekorrektur({
  api, projektId, werte, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  werte: GeoEinstellungen;
  neuLaden: () => void;
}) {
  const [g, setG] = useState<GeoEinstellungen>(werte);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);

  useEffect(() => { setG(werte); }, [werte]);

  const zahl = (wert: string): number | null => {
    const t = wert.trim().replace(',', '.');
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const feld = (name: keyof GeoEinstellungen, beschriftung: string, einheit = 'm') => (
    <>
      <label>{beschriftung}</label>
      <div>
        <input
          value={g[name] === null || g[name] === undefined ? '' : String(g[name])}
          onChange={(e) => setG({ ...g, [name]: zahl(e.target.value) })}
          style={{ width: 160, fontVariantNumeric: 'tabular-nums' }}
        />
        <span className="sehr-schwach" style={{ marginLeft: 8 }}>{einheit}</span>
      </div>
    </>
  );

  const speichern = async () => {
    setFehler(null);
    try {
      await api.geoSpeichern(projektId, g);
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2500);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="karte">
      <h2 className="karte-titel"><IconLage />Lage der IFC-Dateien</h2>

      {fehler && <Fehler text={fehler} />}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={g.aktiv}
          onChange={(e) => setG({ ...g, aktiv: e.target.checked })}
        />
        <span>Lage nach dem Export auf die Vorgabewerte setzen</span>
      </label>

      <p className="sehr-schwach" style={{ maxWidth: 680, marginTop: 0 }}>
        Revit exportiert je nach Einstellung gegen den Vermessungspunkt statt gegen
        den Projektbasispunkt und lässt sich darin nicht immer umstimmen. Ist die
        Korrektur eingeschaltet, werden die Werte in der fertigen Datei überschrieben.
        Die unbearbeitete Fassung bleibt erhalten.
      </p>

      {g.aktiv && (
        <div style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 10, maxWidth: 680 }}>
          {feld('ost', 'Ortspunkt Ost')}
          {feld('nord', 'Ortspunkt Nord')}
          {feld('hoehe', 'Ortspunkt Höhe')}

          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--linie)', margin: '6px 0' }} />

          {feld('refElevation', 'Geländehöhe')}

          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--linie)', margin: '6px 0' }} />

          <label>Koordinatensystem</label>
          <input
            value={g.epsgCode ?? ''}
            onChange={(e) => setG({ ...g, epsgCode: e.target.value || null })}
            placeholder="EPSG:25832"
            style={{ width: 200 }}
          />

          <label>Bezeichnung</label>
          <input
            value={g.crsName ?? ''}
            onChange={(e) => setG({ ...g, crsName: e.target.value || null })}
            placeholder="ETRS89 / UTM zone 32N"
          />

          {feld('weltOst', 'Weltkoordinate Ost')}
          {feld('weltNord', 'Weltkoordinate Nord')}
          {feld('weltHoehe', 'Weltkoordinate Höhe')}
          {feld('weltDrehung', 'Drehung gegen Nord', 'Grad')}

          <label>Vermerk</label>
          <input
            value={g.hinweis ?? ''}
            onChange={(e) => setG({ ...g, hinweis: e.target.value || null })}
            placeholder="Warum die Korrektur nötig ist"
          />
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="betont" onClick={speichern}>Speichern</button>
        {gespeichert && <span style={{ color: 'var(--gut)', fontSize: 13 }}>gespeichert</span>}
      </div>
    </div>
  );
}
